import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Store, store } from '../../../store'
import { getStripe } from '../../../utils/get-stripe'
import { Currency, usePaygProducts } from '../../../utils/payment-utils'
import PaymentsModal from '../../payments/payments-modal'
import ApiClient from '../../../api'

interface BasePaygPurchaseModal {
  show: boolean
  currency?: Currency
  onDismiss?: () => void
  onPaymentSuccess?: () => void
}

interface PaygPurchaseModal extends BasePaygPurchaseModal {
  apiClient: ApiClient
  stripeToken: string
}

export default function StoreBackedPaygPurchaseModal(
  props: BasePaygPurchaseModal
): JSX.Element {
  const {
    state: { apiClient, stripeToken },
  } = useContext(store) as Store

  return (
    <PaygPurchaseModal
      apiClient={apiClient}
      stripeToken={stripeToken}
      {...props}
    />
  )
}

export function PaygPurchaseModal({
  show,
  currency,
  onDismiss,
  onPaymentSuccess,
  apiClient,
  stripeToken,
}: PaygPurchaseModal): JSX.Element {
  const { paygProducts, hasSubscription } = usePaygProducts(apiClient)
  const [initialized, setInitialized] = useState(false)
  useEffect(() => {
    if (hasSubscription != null && !hasSubscription) {
      window.location.href = '/subscriptions'
      if (onDismiss) {
        onDismiss()
      }
    } else {
      setInitialized(hasSubscription != null)
    }
  }, [hasSubscription, onDismiss])

  return (
    <PaymentsModal
      title="Buy More Credits"
      instructions={
        <h3 className="tw-custom-reset-heading tw-font-medium tw-mb-3">
          Select Credit Amount
        </h3>
      }
      products={paygProducts}
      currency={currency}
      show={initialized && show}
      onDismiss={
        onDismiss ||
        (() => {
          // Do nothing
        })
      }
      onPaymentSuccess={onPaymentSuccess}
      createSubscription={apiClient.subscriptions.create.bind(
        apiClient.subscriptions
      )}
      getPurchaseUrl={apiClient.user.getPurchaseUrl.bind(apiClient.user)}
      getUserPaymentMethod={apiClient.user.defaultPaymentMethod.bind(
        apiClient.user
      )}
      getStripe={() => getStripe(stripeToken)}
    />
  )
}

interface UsePurchasePaygState {
  show: boolean
  callback: () => Promise<void>
  onDismiss: () => void
}

export function usePurchasePayg(): UsePurchasePaygState {
  const promiseResolve = useRef<() => void>()
  const [show, setShow] = useState(false)
  const callback = useCallback(() => {
    setShow(true)

    if (promiseResolve.current) {
      console.warn("PAYG purchase modal triggered while it's already active.")
    }

    return new Promise<void>(resolve => {
      promiseResolve.current = resolve
    })
  }, [])
  const onDismiss = useCallback(() => {
    if (promiseResolve.current) {
      promiseResolve.current()
      promiseResolve.current = undefined
    }
    setShow(false)
  }, [])

  return { show, callback, onDismiss }
}
